import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import './Admin.css';

interface CreditEntry {
    id: number;
    amount: number;
    type: string;
    reason: string;
    referenceId: number;
    date: string;
}

interface UserCreditData {
    user: { id: number; mobile: string };
    balance: number;
    ledger: CreditEntry[];
}

interface UserSummary {
    id: number;
    mobile: string;
    balance: number;
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

const AdminCredits: React.FC = () => {
    const [searchMobile, setSearchMobile] = useState('');
    const [searchedUser, setSearchedUser] = useState<UserCreditData | null>(null);
    const [searchLoading, setSearchLoading] = useState(false);

    const [users, setUsers] = useState<UserSummary[]>([]);
    const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 10, total: 0, totalPages: 0 });
    const [usersLoading, setUsersLoading] = useState(false);

    useEffect(() => {
        fetchUsers(1);
    }, []);

    const fetchUsers = async (page: number) => {
        setUsersLoading(true);
        try {
            const response = await axios.get(`/api/credit/admin/users?page=${page}&limit=10`);
            setUsers(response.data.users);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Failed to fetch users:', error);
        } finally {
            setUsersLoading(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchUsers(newPage);
        }
    };

    const handleSearchUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!searchMobile) return;

        setSearchLoading(true);
        try {
            const response = await axios.get(`/api/credit/admin/search-user?mobile=${searchMobile}`);
            setSearchedUser(response.data);
            // Scroll to search section if possible, or just let the user see it
        } catch (error) {
            console.error('Failed to search user:', error);
            alert('User not found or error occurred');
            setSearchedUser(null);
        } finally {
            setSearchLoading(false);
        }
    };

    // Helper to trigger search from list
    const fetchUserHistory = (mobile: string) => {
        setSearchMobile(mobile);
        // Programmatically trigger search logic
        setSearchLoading(true);
        axios.get(`/api/credit/admin/search-user?mobile=${mobile}`)
            .then(response => {
                setSearchedUser(response.data);
                const searchElement = document.getElementById('search-section');
                if (searchElement) searchElement.scrollIntoView({ behavior: 'smooth' });
            })
            .catch(error => {
                console.error('Failed to search user:', error);
                alert('User not found or error occurred');
                setSearchedUser(null);
            })
            .finally(() => setSearchLoading(false));
    };

    return (
        <>
            <Navbar />
            <div className="container">
                <h1 className="page-title">💰 Credit System</h1>

                <div className="card" id="search-section">
                    <h2>🔍 User Credit Search</h2>
                    <form onSubmit={handleSearchUser} style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                        <input
                            type="text"
                            placeholder="Enter User Mobile Number"
                            value={searchMobile}
                            onChange={(e) => setSearchMobile(e.target.value)}
                            className="form-control"
                            style={{ flex: 1, padding: '10px' }}
                        />
                        <button type="submit" className="btn btn-primary" disabled={searchLoading}>
                            {searchLoading ? 'Searching...' : 'Search'}
                        </button>
                    </form>

                    {searchedUser && (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3>User: {searchedUser.user.mobile}</h3>
                                <span className="stat-value" style={{ fontSize: '1.5rem', color: searchedUser.balance >= 0 ? '#28a745' : '#dc3545' }}>
                                    Balance: {searchedUser.balance}
                                </span>
                            </div>

                            <h4>History</h4>
                            <div className="table-container">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Amount</th>
                                            <th>Type</th>
                                            <th>Reason</th>
                                            <th>Ref ID</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {searchedUser.ledger.length === 0 ? (
                                            <tr><td colSpan={5} style={{ textAlign: 'center' }}>No history found</td></tr>
                                        ) : (
                                            searchedUser.ledger.map((entry) => (
                                                <tr key={entry.id}>
                                                    <td>{new Date(entry.date).toLocaleString()}</td>
                                                    <td style={{ color: entry.amount > 0 ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                                                        {entry.amount > 0 ? '+' : ''}{entry.amount}
                                                    </td>
                                                    <td>{entry.type}</td>
                                                    <td>{entry.reason.replace('_', ' ')}</td>
                                                    <td>{entry.referenceId}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                <div className="card">
                    <h2>👥 User List</h2>
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Mobile</th>
                                    <th>Credit Balance</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {usersLoading ? (
                                    <tr><td colSpan={3} style={{ textAlign: 'center' }}>Loading users...</td></tr>
                                ) : users.length === 0 ? (
                                    <tr><td colSpan={3} style={{ textAlign: 'center' }}>No users found</td></tr>
                                ) : (
                                    users.map((user) => (
                                        <tr key={user.id}>
                                            <td>{user.mobile}</td>
                                            <td style={{ color: user.balance >= 0 ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                                                {user.balance}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-secondary"
                                                    style={{ padding: '5px 10px', fontSize: '0.9rem' }}
                                                    onClick={() => fetchUserHistory(user.mobile)}
                                                >
                                                    View History
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {pagination.totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginTop: '20px' }}>
                            <button
                                className="btn btn-secondary"
                                disabled={pagination.page === 1 || usersLoading}
                                onClick={() => handlePageChange(pagination.page - 1)}
                            >
                                Previous
                            </button>
                            <span>Page {pagination.page} of {pagination.totalPages}</span>
                            <button
                                className="btn btn-secondary"
                                disabled={pagination.page === pagination.totalPages || usersLoading}
                                onClick={() => handlePageChange(pagination.page + 1)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>

                <div className="card">
                    <h3>Credit Rules</h3>
                    <ul>
                        <li>+9 credits when a warranty is activated</li>
                        <li>-9 credits when a claim is approved (deducted only once per warranty)</li>
                        <li>No credit impact for rejected claims</li>
                    </ul>
                </div>
            </div>
        </>
    );
};

export default AdminCredits;
